import React, { useEffect, useRef, useState } from 'react'
import { CircleMetrics, Col, Container, Row, Search, TopBar } from 'components'
import { useTranslation } from 'react-i18next'
import './fcHome.scss'
import { useOnChange, useBindDispatch, useValidation } from 'hooks'
import { CSSTransition } from 'react-transition-group'
import {
    constants,
    getConnectUser,
    isFieldAgent,
    quickSearchValidationRules,
    searchBy
} from 'helpers'
import { useSelector } from 'react-redux'
import { RootState } from 'store/configStore'
import { useNavigate } from 'react-router-dom'
import { InputErrorMessage } from 'components/enrollForm/InputErrorMessage'
import { colors } from 'styles/colors'

export const FcHome = () => {
    const [animate, setAnimate] = useState(false)
    const [animationList, setAnimationList] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const nodeRef = useRef(null)
    const navigate = useNavigate()
    const [changeIconColorTo, setChangeIconColorTo] = useState('')
    const { inputText, onChangeHandler } = useOnChange({
        query: ''
    })
    const fcData = useSelector((state: RootState) => state.fcMetrics?.data)
    const { ves, villages } = useSelector((state: RootState) => state.ves)
    const { t } = useTranslation()
    const {
        fcMetricsHandler,
        fetchVesHandler,
        fetchAssignedVillagesHandler,
        saveVeSearchResultsHandler,
        fetchCountryEnrollmentHandler,
        fetchCountryConfigHandler
    } = useBindDispatch()
    const { staff_code: staffCode, user_id: userId, role } = getConnectUser()
    const fieldAgent = isFieldAgent(role)

    const { ANIMATION_TIMEOUT } = constants

    const searchValidationRules = quickSearchValidationRules()

    const { errors, validateForm } = useValidation(
        inputText,
        searchValidationRules
    )

    useEffect(() => {
        setAnimate(true)

        if (!fcData && staffCode) {
            fcMetricsHandler(staffCode)
        }
        fetchCountryEnrollmentHandler()
    }, [fcData, staffCode])

    // ATC enrolment needs operators; mount-only so fcData refresh does not re-fetch
    useEffect(() => {
        if (fieldAgent) {
            fetchCountryConfigHandler()
        }
    }, [fieldAgent])

    useEffect(() => {
        localStorage.removeItem(constants.fcSearchResults)
        if (fieldAgent) {
            if (userId) {
                fetchAssignedVillagesHandler(userId)
            }
            return
        }
        if (fcData?.fc_info?.zone_code) {
            fetchVesHandler(fcData.fc_info.zone_code)
        }
    }, [fcData, fieldAgent, userId])

    useEffect(() => {
        if (inputRef.current) {
            setChangeIconColorTo(colors.primary)
        }
    }, [inputText.query, inputRef.current])

    const metrics = [
        {
            label: 'Villages',
            value: villages?.length ?? 0
        },
        {
            label: 'VEs',
            value: ves?.length ?? 0
        },
        {
            label: 'Producteurs',
            value: fcData?.fc_metrics?.total_farmers ?? 0
        }
    ]

    const handleVeSearch = () => {
        if ((!inputText.query || !!errors.query) && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.style.border = `1px solid ${colors.red}`
            return setChangeIconColorTo(colors.red)
        }
        const searchResults = searchBy(
            ves,
            ['name', 'code', 'current_villages'],
            inputText.query
        )

        saveVeSearchResultsHandler(searchResults)

        navigate('/fc/ve-search/results')
    }

    useEffect(() => {
        validateForm()
        if (inputRef.current) {
            inputRef.current.style.border = 'none'
            setChangeIconColorTo(colors.primary)
        }
    }, [inputText.query, inputRef.current])

    return (
        <div>
            <div className="sticky-header" id="sticky-header">
                <TopBar color={colors.black}>
                    {t('zone')} {fcData?.fc_info?.zone_name}
                </TopBar>
            </div>
            <CSSTransition
                unmountOnExit
                timeout={ANIMATION_TIMEOUT}
                in={animate}
                classNames="generic"
                appear
                onEnter={() => setAnimationList(true)}
                nodeRef={nodeRef}
            >
                <div className="page-wrapper" ref={nodeRef}>
                    <Container aria-label="fc-home">
                        <Row>
                            <Col
                                md={12}
                                className={
                                    animationList
                                        ? 'animate-showed-1'
                                        : 'animate-show-1'
                                }
                            >
                                <p
                                    className="fc-home--welcome"
                                    aria-label="welcome-header"
                                >
                                    {t('home.greetings')}
                                    {fcData?.fc_info?.name
                                        ? `, ${
                                              fcData?.fc_info?.name?.split(
                                                  ' '
                                              )[0]
                                          }`
                                        : ''}
                                </p>
                            </Col>
                            <Col
                                md={12}
                                className={
                                    animationList
                                        ? 'animate-showed-4'
                                        : 'animate-show-4'
                                }
                            >
                                {<CircleMetrics metrics={metrics} />}
                            </Col>
                            <Col
                                md={12}
                                className={
                                    animationList
                                        ? 'animate-showed-6'
                                        : 'animate-show-6'
                                }
                            >
                                <div className="fc-home--search">
                                    <Search
                                        inputRef={inputRef}
                                        iconSize={'30'}
                                        iconColor={changeIconColorTo}
                                        iconPos="left"
                                        style="rounded"
                                        onChange={onChangeHandler}
                                        placeholder={t('veSearch.placeholder')}
                                        data-testid="quick-search-input"
                                        onSearchHandler={handleVeSearch}
                                    />
                                    {errors?.query && (
                                        <InputErrorMessage
                                            errorMessage={errors?.query}
                                        />
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </CSSTransition>
        </div>
    )
}
